import React, { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

import { StoreLocatorEmptyStates } from 'features/store/components/store-locator-empty-states';
import { IStore, mapStoreResponse } from 'features/store/pages/pickup-store-selector/ducks/stores';
import isEqual from 'lodash-es/isEqual';
import { useIntl } from 'react-intl';
import { FlatList } from 'react-native';

import { IRestaurant } from '@rbi-ctg/store';
import { ClickEventComponentNames, CustomEventNames, logButtonClick } from 'state/amplitude';
import { useGeolocation } from 'state/geolocation';
import { OrderLocatorTab } from 'state/launchdarkly/variations';

import { LoadingIndicator } from '../loading-indicator';
import { StoreCard } from '../store-card';
import {
  FocusedBy,
  OnRestaurantHighlight,
  StoreCardListRef,
  StoreLocatorActionType,
  StoreLocatorActions,
} from '../types';

interface IStoreCardListProps {
  currentTabHasError?: boolean;
  isSearching?: boolean;
  restaurants: IStore[];
  tab: OrderLocatorTab;
  onCardPress?: (storeId?: string, storeNumber?: string, storeAddress?: string) => void;
  onSelectStore?: (storeId: string) => void;
  isInSupportForm?: boolean;
  restaurantFocused: IStore | null;
  onRestaurantHighlight: OnRestaurantHighlight;
  storesById: Record<string, IRestaurant>;
  onFavoriteClick: () => void;
  actions: React.MutableRefObject<StoreLocatorActions>;
}

const StoreCardList = forwardRef<StoreCardListRef, IStoreCardListProps>(
  (
    {
      currentTabHasError = false,
      isSearching,
      restaurants = [],
      tab,
      onSelectStore,
      isInSupportForm,
      restaurantFocused,
      onRestaurantHighlight,
      storesById,
      onFavoriteClick,
      actions,
    },
    ref
  ) => {
    const { formatMessage } = useIntl();
    const { coordinatesAvailable, isPermissionGranted } = useGeolocation();
    const flatListRef = useRef<FlatList | null>(null);

    const getStoreById = useCallback(
      (storeId: string) => {
        return storesById?.[storeId];
      },
      [storesById]
    );

    const handleOnCardPress = useCallback(
      ({ restaurant }: { restaurant: IRestaurant }) => {
        actions.current[StoreLocatorActionType.USER_SELECTION] = true;
        const restaurantToIStore = mapStoreResponse(restaurant);
        onRestaurantHighlight({ restaurant: restaurantToIStore, focusedBy: FocusedBy.LIST });
        if (restaurant._id) {
          logButtonClick({
            attributes: {
              StoreId: restaurant._id,
              Name: CustomEventNames.BUTTON_CLICK_STORE_SELECTED,
              name: CustomEventNames.BUTTON_CLICK_STORE_SELECTED,
              component: ClickEventComponentNames.BUTTON,
            },
          });
        }
      },
      [actions, onRestaurantHighlight]
    );

    const scrollToTop = useCallback(() => {
      if (!flatListRef.current || !restaurants.length) {
        return;
      }

      // small delay to ensure the list is rendered
      setTimeout(() => {
        flatListRef.current?.scrollToOffset({ animated: true, offset: 0 });
      }, 100);
    }, [restaurants]);

    const scrollToRestaurant = useCallback(
      (restaurantId: string) => {
        if (!flatListRef.current || !restaurants.length) {
          return;
        }

        const restaurantIndex = restaurants.findIndex(
          restaurant => restaurant._id === restaurantId
        );
        if (restaurantIndex !== -1) {
          flatListRef.current?.scrollToIndex({
            animated: true,
            index: restaurantIndex,
            viewPosition: 0.5,
          });
        }
      },
      [restaurants]
    );

    useImperativeHandle(
      ref,
      () => ({
        scrollToTop,
        scrollToRestaurant,
      }),
      [scrollToRestaurant, scrollToTop]
    );

    const renderItem = useCallback(
      ({ item, index }: { item: IStore; index: number }) => {
        const isLastItem = index === restaurants.length - 1;
        const store = item._id ? getStoreById(item._id) : null;

        if (!store?._id) {
          return null;
        }

        return (
          <StoreCard
            restaurant={store}
            onFavoriteClick={onFavoriteClick}
            onCardPress={handleOnCardPress}
            isSelected={item._id === restaurantFocused?._id}
            isInSupportForm={isInSupportForm}
            isLastItem={isLastItem}
            onSelectStore={onSelectStore}
          />
        );
      },
      [
        restaurants.length,
        getStoreById,
        restaurantFocused?._id,
        onSelectStore,
        isInSupportForm,
        handleOnCardPress,
        onFavoriteClick,
      ]
    );

    if (isSearching) {
      return <LoadingIndicator ariaLabel={formatMessage({ id: 'locationSearch' })} />;
    }

    if (restaurants.length === 0) {
      return (
        <StoreLocatorEmptyStates
          tab={tab}
          locationPermissionsGranted={isPermissionGranted}
          coordinatesAvailable={coordinatesAvailable}
          currentTabError={currentTabHasError}
          wrapperStyle={{
            width: '100%',
          }}
        />
      );
    }

    if (restaurants.length === 0) {
      return <LoadingIndicator ariaLabel={formatMessage({ id: 'locationSearch' })} />;
    }

    return (
      <FlatList
        keyExtractor={(item, index) => item?._id || index + '_restaurant'}
        data={restaurants}
        renderItem={renderItem}
        ref={flatListRef}
      />
    );
  }
);

export default React.memo(StoreCardList, (prevProps, nextProps) => {
  if (prevProps.restaurantFocused !== nextProps.restaurantFocused) {
    return false;
  }
  if (prevProps.tab !== nextProps.tab) {
    return false;
  }
  if (prevProps.isSearching !== nextProps.isSearching) {
    return false;
  }
  if (prevProps.currentTabHasError !== nextProps.currentTabHasError) {
    return false;
  }
  if (
    !isEqual(
      prevProps?.restaurants?.map(x => x._id),
      nextProps?.restaurants?.map(x => x._id)
    )
  ) {
    return false;
  }

  if (prevProps.ref !== nextProps.ref) {
    return false;
  }

  return true;
});
